import type { AttachmentAdapter, AttachmentRef, DocumentId } from "../index";

/** What a composer has been given to send with its message, and how many more are on their way. */
export interface TrayState {
  attachments: readonly AttachmentRef[];
  /** Files the host's store is still keeping: not in the tray yet, and not to be left behind. */
  staging: number;
}

/**
 * A composer's attachments. They are not in its document: a message's files go with it, beside
 * its text, and reach the host as `ComposerSubmission.attachments`. The tray is kept between
 * visits by the host's store (`adapter.pending`), where the files themselves already are, and
 * never by the document, which is only ever what the text says.
 */
export interface TrayStore {
  subscribe(listener: () => void): () => void;
  getState(): TrayState;
  /** Goes up with every change to what is in the tray: the message is another one then. */
  getRevision(): number;
  add(attachment: AttachmentRef): void;
  remove(assetIds: readonly string[]): void;
  setStaging(staging: number): void;
  /** What the host's store kept from last time, unless the tray has been touched meanwhile. */
  restore(): void;
}

const EMPTY: TrayState = { attachments: [], staging: 0 };

export function createTrayStore(
  documentId: DocumentId,
  adapter: () => AttachmentAdapter | null,
): TrayStore {
  const listeners = new Set<() => void>();
  let state = EMPTY;
  let revision = 0;
  const set = (next: TrayState): void => {
    state = next;
    for (const listener of listeners) listener();
  };
  const change = (attachments: readonly AttachmentRef[]): void => {
    revision++;
    set({ ...state, attachments });
    // The whole list, every time. A store that fails to keep it costs the tray its memory and
    // nothing else, so there is nobody to tell.
    void adapter()
      ?.pending?.set(documentId, attachments)
      .catch(() => {});
  };
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getState: () => state,
    getRevision: () => revision,
    add(attachment) {
      if (state.attachments.some((held) => held.assetId === attachment.assetId)) return;
      change([...state.attachments, attachment]);
    },
    remove(assetIds) {
      const gone = new Set(assetIds);
      const kept = state.attachments.filter((held) => !gone.has(held.assetId));
      if (kept.length !== state.attachments.length) change(kept);
    },
    setStaging(staging) {
      if (staging !== state.staging) set({ ...state, staging });
    },
    restore() {
      const asOf = revision;
      void adapter()
        ?.pending?.list(documentId)
        .then(
          (kept) => {
            if (revision !== asOf || kept.length === 0) return;
            revision++;
            set({ ...state, attachments: [...kept] });
          },
          () => {},
        );
    },
  };
}
